// 치과 환자관리 — Cloud Functions 호출 모음 (2026-09-10 1단계)
// 서버 계약: docs/구현_환자관리_서버_1단계_2026-09-10.md §3 (대조 완료 2026-09-10). 계약이 바뀌면 이 파일 한 곳만 고친다.
// 화면 컴포넌트는 CF 이름·입출력 형태를 직접 알지 않는다.
//
// 공통: region asia-northeast3, v1 onCall. 플래그(config/features.clinicPatients) OFF면 모든 onCall이
//       failed-precondition('치과 환자관리 기능이 아직 열리지 않았습니다') — 웹 베타 미리보기도 서버 플래그가 ON이어야 실동작.
// 오류: e.code = 'functions/<code>', e.details = { code:'phone-link-required'|'under-age'|'consent-required', reason? }
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { effectiveDocs } from '../config/consentVersions';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);

// 오류 응답 단일 문구 (보안검토 P2-2 oracle 단일화) — 존재 여부·상태를 문구로 구분하지 않는다
export const GENERIC_ERROR = '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
export const INVALID_QR = '유효하지 않은 QR입니다. 치과에 문의해 주세요';

// CF 오류를 화면 문구로. 원본은 여기서 console.error
// linkOrderToPatient failed-precondition 'app-link-check-required'(10-04 서버 계약) — 앱 등록 환자 확인 창(L-6) 시각이 없거나 3시간이 지남
export const APP_LINK_CHECK_REQUIRED_TEXT = '앱 등록 환자 확인 후 다시 연결해 주세요. (환자를 고를 때 나오는 확인 창을 누른 지 3시간이 지나면 다시 확인해야 합니다)';

export function describeError(e, fallback = GENERIC_ERROR) {
    console.error('[clinicPatientsApi]', e?.code || '', e?.message || e, e?.details || '');
    if (isAppCheckError(e)) return APP_CHECK_REQUIRED_TEXT;   // 서버 App Check 강제 시
    if (e?.code === 'functions/unauthenticated') return '로그인이 필요합니다.';
    if (e?.code === 'functions/permission-denied') return e?.message || '권한이 없습니다.';
    if (e?.code === 'functions/resource-exhausted') return '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.';
    if (e?.code === 'functions/failed-precondition') {
        const c = e?.details?.code;
        if (c === 'under-age') return '만 14세 이상만 등록할 수 있습니다.';
        if (c === 'consent-required') return '필수 동의 항목을 모두 확인해 주세요.';
        if (c === 'docs-consent-required') return DOCS_CONSENT_REQUIRED;
        if (c === 'docs-pref-off') return DOCS_PREF_OFF;
        if (DOCS_REQUEST_ERROR_TEXT[c]) return DOCS_REQUEST_ERROR_TEXT[c];
        if (c === 'docs-request-too-soon') return DOCS_REQUEST_TOO_SOON_TEXT;
        // 등록 경로 v2 자가등록 확인·거절 (계약 docs/계약_등록경로v2_서버_2026-10-01.md §A-3)
        if (c === 'record-not-confirmable') return '확인할 수 있는 상태가 아닙니다. 목록을 새로고침해 주세요.';
        if (c === 'care-gate-closed') return '환자 앱 등록 처리위탁 확인이 유효하지 않아 앱 등록 환자에게 연결할 수 없습니다. 치과 대표가 환자 관리 화면에서 처리위탁 확인 상태를 확인해 주세요.';
        if (c === 'app-link-check-required') return APP_LINK_CHECK_REQUIRED_TEXT;
        if (c === 'record-already-confirmed') return '이미 확인된 환자입니다. 삭제는 상세 화면의 [환자 삭제]로 해 주세요.';
        if (!c) return e?.message || fallback;   // 플래그 OFF 등 서버 메시지 그대로
    }
    if (e?.code === 'functions/not-found') return '환자 정보를 찾을 수 없습니다. 목록을 새로고침해 주세요.';
    if (e?.code === 'functions/invalid-argument') return e?.message || '입력값을 확인해 주세요.';
    return fallback;
}

// 휴대폰 인증 연결이 필요한 응답인지 (카카오 세션인데 Auth 레코드에 번호 없음 — 계약 §3-3)
export function isPhoneRequired(e) {
    return e?.code === 'functions/failed-precondition' && e?.details?.code === 'phone-link-required';
}

// §3-1 — 비로그인 허용. 실패는 예외가 아니라 { ok:false, message }
export async function resolveClinicQr(token) {
    const res = await call('resolveClinicQr')({ token });
    return res.data || { ok: false };
}

// §3-3 — payload: { token, name, birthDate, gender, note, underAgeAttested, consents:{collect|sensitive|delegation|notify:{agreed,version}}, channel }
//         response: { pid, clinicId, clinicName, existing, phoneVerifiedBy, fcmRegistered }
export async function registerClinicPatient(payload) {
    const res = await call('registerClinicPatient')(payload);
    return res.data || {};
}

// §3-5 — response: { records:[{ clinicId, clinicName, pid, status, fcmRegistered, notify, cases:[...] }] }
//         cases[i]: { orderId, stageKey, stageLabel, step, packed, packedLabel, teeth[], prosthesis[], material[], labName?, orderedAt, arrivedAt, packedAt, linkedAt }
//         단계 판정은 서버(stageLabel·packedLabel) — 웹에서 재판정하지 않는다
export async function getMyPatientCases(clinicId) {
    const res = await call('getMyPatientCases')(clinicId ? { clinicId } : {});
    return res.data || { records: [] };
}

// §3-4 — 환자 본인 삭제 요청(status → deleteRequested, 파기는 오너)
export async function requestPatientDeletion(clinicId) {
    const res = await call('requestPatientDeletion')({ clinicId });
    return res.data || {};
}

// §3-6 — response: { record:{...}, cases:[...], memos:[{ id, text, kind, createdBy, createdAt, supersedes }] }
export async function getClinicPatientDetail(pid) {
    const res = await call('getClinicPatientDetail')({ pid });
    return res.data || {};
}

// §3-7
export async function addPatientMemo(pid, text, supersedes) {
    const res = await call('addPatientMemo')(supersedes ? { pid, text, supersedes } : { pid, text });
    return res.data || {};
}

// §3-8 — response: { ok, already, push:{ result } }
//   extra.appLinkCheck(덴클 케어 W9 — 정본 v2 L-6 확인 창 [확인하고 연결] 시각 ms, 숫자) — 서버가 접속기록에 남긴다
export async function linkOrderToPatient(orderId, pid, extra) {
    const res = await call('linkOrderToPatient')({ orderId, pid, ...(extra && Number.isFinite(extra.appLinkCheck) ? { appLinkCheck: extra.appLinkCheck } : {}) });
    return res.data || {};
}

// 2026-10-17 점검 P1-1 — 환자 상세 [의뢰서 연결]: 아직 환자와 이어지지 않은 이 치과 의뢰서(서버 listUnlinkedClinicOrders, 접속기록 unlinked_orders_list)
//   입력 { days?(1~730, 기본 180), limit?(≤100), nameQuery?(≤20자), client:'web' } · 응답 { ok, count, scanned, truncated, items:[{orderId, orderNumber, patientName, labName, createdAt(ms), dueDate(ms|null), status}] }
//   반려·취소·임시저장·메시지·이미 연결·보낸 쪽 숨김은 서버가 뺀다. 웹은 workOrders 를 직접 읽지 않는다(검수 P2-1)
export async function listUnlinkedClinicOrders({ days, limit, nameQuery } = {}) {
    const payload = { client: 'web' };
    if (Number.isInteger(days)) payload.days = days;
    if (Number.isInteger(limit)) payload.limit = limit;
    const q = typeof nameQuery === 'string' ? nameQuery.trim().slice(0, 20) : '';
    if (q) payload.nameQuery = q;
    const res = await call('listUnlinkedClinicOrders')(payload);
    return res.data || { ok: false, items: [], count: 0, truncated: false };
}

// §3-9
export async function unlinkOrderFromPatient(orderId, pid) {
    const res = await call('unlinkOrderFromPatient')({ orderId, pid });
    return res.data || {};
}

// §3-10 — 오너만. reason 은 enum 필수(서버 §0-0 P2-6), 그 외 invalid-argument
export const DELETE_REASONS = [
    { value: 'patient_request', label: '환자 요청' },
    { value: 'duplicate', label: '중복 등록' },
    { value: 'wrong_entry', label: '잘못 등록' },
    { value: 'other', label: '기타' },
    { value: 'not_our_patient', label: '우리 환자 아님' },   // 2026-10-04 서버 추가(대표 전용) — 덴클 케어 앱 등록 삭제(정본 v2 L-7)
];
// 환자 측 삭제 요청 사유(status deleteRequested — 계약 §3-4·§3-15). 사유 미기록 구버전 레코드는 '환자 요청'으로 표시
export const DELETE_REQUEST_REASON_LABEL = { account_delete: '환자앱 계정 삭제', patient_request: '환자 요청' };
export const deleteRequestReasonLabel = (v) => DELETE_REQUEST_REASON_LABEL[v] || DELETE_REQUEST_REASON_LABEL.patient_request;
export async function deleteClinicPatient(pid, reason) {
    if (!DELETE_REASONS.some(r => r.value === reason)) throw new Error('삭제 사유를 선택해 주세요.');
    const res = await call('deleteClinicPatient')({ pid, reason });
    return res.data || {};
}

// §3-2 — 오너만. reissue:false = 미발급이면 발급, 있으면 현재값 반환('get' 별도 없음)
//         response: { token, tokenHash, issuedAt(ms), reissued, url:'/p/c/{token}' }
export async function issueClinicQr(reissue = false) {
    const res = await call('issueClinicQr')({ reissue: reissue === true });
    return res.data || {};
}

// ══════════════════════════════════════════════════════════════════════
// 서류함 (P2-12, 2026-09-14) — docs/기획_치과환자관리_전체지도_2026-09-11.md §5-2-12 · docs/법무보완_서류함_2026-09-14.md
// 서버 계약 docs/구현_서류함_서버_2026-09-14.md §1-1과 대조 완료(2026-09-14). 정본 키 kind·sizeBytes, 종류 코드 receipt|detail|diagnosis|other.
//   아래 D-1~D-7 은 최초 가정 기록 — 서버가 별칭(type·size·평탄 patientName)을 함께 반환해 호환됨.
//   D-1 issueDocUploadUrl({ pid, type, contentType, size, issuedAt? })
//        → { docId, uploadUrl, method:'PUT', headers?:{}, patient:{name, phoneLast4}, patientName, phoneLast4(평탄 별칭), expiresAt }
//          (확인 모달의 환자 이름·뒤4자리는 이 응답값을 쓴다 — 화면이 가진 레코드 값이 아니라 서버가 실제 올릴 대상)
//        실패: failed-precondition details.code 'docs-consent-required' (환자 consents.docs 없음)
//   D-2 서명 URL PUT 업로드 — Content-Type = 파일 contentType, 본문 = 파일. (GCS V4 signed URL 가정: 서명 시 contentType 고정)
//   D-3 finalizeClinicPatientDoc({ pid, docId }) → { ok, doc } — 서버가 Storage 객체 존재·크기·contentType 검증 후 메타 생성·푸시·logAccess doc_upload
//   D-4 listClinicPatientDocs({ pid }) → { docs:[{ docId, type, contentType, size, issuedAt, uploadedBy, uploadedRole, createdAt }] }
//        (getClinicPatientDetail 응답에 docs[]가 실리면 그것을 우선 쓰고 이 호출은 생략 — 화면 쪽에서 분기)
//   D-5 issueDocDownloadUrl({ pid, docId }) → { url, expiresAt } — 서명 URL 10분, logAccess doc_view
//   D-6 deleteClinicPatientDoc({ pid, docId, reason }) → { ok } — 오너만, reason enum(DOC_DELETE_REASONS)
//   D-7 명부 카운트: patients/{pid}.docCount (서버가 finalize/delete 시 증감) — 계약에 없으면 배지는 자연히 미표시
export const DOCS_CONSENT_REQUIRED = '이 환자는 서류 사본 수신에 동의하지 않았습니다. 서류를 올릴 수 없습니다.'   // 부록 A-9 사실 안내안;
export const DOC_MAX_BYTES = 20 * 1024 * 1024;
export const DOC_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
// 종류 — 진단서·소견서(diagnosis)는 민감정보 재동의 이슈로 플래그 docsDiagnosisEnabled ON일 때만 선택지에 노출(법무보완 §3)
export const DOC_TYPES = [
    { value: 'receipt', label: '진료비 영수증' },
    { value: 'detail', label: '진료비 세부내역서' },   // 서버 정본 코드 detail (계약 §1-1 — statement 아님)
    { value: 'diagnosis', label: '진단서·소견서', flagged: true },
    { value: 'other', label: '기타' },
];
export const docTypeLabel = (v) => (DOC_TYPES.find(t => t.value === (v === 'statement' ? 'detail' : v)) || {}).label || '서류';
export const DOC_DELETE_REASONS = [
    { value: 'misupload', label: '잘못 올림(다른 환자·다른 서류)' },
    { value: 'patient_request', label: '환자 요청' },
    { value: 'reissued', label: '재발급으로 대체' },
    { value: 'other', label: '기타' },
];
// 명세 하단 고정 문구 (§5-2-12·법무보완 §4 — 문구 변경 시 법적보안 확인)
export const DOCS_COPY_NOTICE = '덴탈클러스터는 치과가 발급한 서류의 사본을 보관·전달만 합니다. 원본은 치과에 있습니다.';

// ── 서류 받기 선택 요청 (2026-09-16 환자기능확장 — 서버 계약 docs/구현_환자기능확장_서버_2026-09-16.md §2-2) ──
// 문구 정본: docs/환자동의_문안_cp-1.3_정본_2026-09-16.md §5-4 · cp-1.4 정본 §4-2. 문구 변경 시 법적보안 확인.
// 금지: "동의를 받아 주세요"·"꼭 동의하도록 안내" 등 데스크가 환자를 설득하도록 유도하는 문구.
export const DOCS_PREF_OFF = '환자가 서류 사본 받기를 껐습니다. 서류를 올리거나 요청할 수 없습니다.';
export const DOCS_REQUEST_TEXT = {
    canRequest: '이 환자는 서류 사본 수신에 동의하지 않았습니다. 환자 앱으로 선택 요청을 보낼 수 있습니다.',
    revoked: '환자가 서류 사본 수신을 철회했습니다. 서류를 올리거나 요청할 수 없습니다.',
    notLinked: '환자가 앱에 연결되지 않아 요청할 수 없습니다.',
    // record-not-active — 환자 상태별(cp-1.4 정본 부록 A-2)
    notActive: '현재 환자 상태에서는 요청할 수 없습니다.',
    notActiveDeleteRequested: '등록 삭제 요청 중인 환자에게는 요청할 수 없습니다.',
    notActiveDeleted: '삭제된 환자에게는 요청할 수 없습니다.',
    // 부록 A-1. 서버 config/features.patientDocsRequestMaxDeclines 설정 시에만 blockedReason 으로 옴(미설정=제한 없음 → 화면에 안 나옴)
    declinedLimit: '환자가 서류 받기를 받지 않기로 선택하여 더 요청할 수 없습니다.',
    intervalNote: '같은 환자에게는 30일에 한 번 요청할 수 있습니다.',
};
export const DOCS_REQUEST_TOO_SOON_TEXT = '같은 환자에게는 30일에 한 번 요청할 수 있습니다.';
const DOCS_REQUEST_ERROR_TEXT = {
    'record-not-active': DOCS_REQUEST_TEXT.notActive,
    'patient-app-not-registered': DOCS_REQUEST_TEXT.notLinked,
    'docs-already-agreed': '이미 서류 사본을 받는 환자입니다.',
    'docs-consent-revoked': DOCS_REQUEST_TEXT.revoked,
    'docs-request-declined-limit': DOCS_REQUEST_TEXT.declinedLimit,
};

// requestPatientDocsConsent({pid}) → { ok, requestId, requestedAt(ms), nextAvailableAt(ms), push:{result:'sent'|'no_consent'|'no_token'|'failed'|'disabled'} }
// 오류: failed-precondition details.code(위 표 + docs-pref-off·docs-request-too-soon{nextAvailableAt}) / not-found / permission-denied / resource-exhausted(치과당 하루 300)
export async function requestPatientDocsConsent(pid) {
    const res = await call('requestPatientDocsConsent')({ pid });
    return res.data || {};
}

// 요청 실패 오류를 화면 문구로 — 이 호출 전용(하루 상한 문구 구분)
export function describeDocsRequestError(e) {
    if (e?.code === 'functions/resource-exhausted') { console.error('[clinicPatientsApi] docs request limit', e?.message || e); return '오늘 이 치과에서 보낼 수 있는 요청 수를 넘었습니다. 내일 다시 시도해 주세요.'; }
    if (e?.code === 'functions/not-found') { console.error('[clinicPatientsApi] docs request not-found', e?.message || e); return '환자 정보를 찾을 수 없습니다. 목록에서 다시 열어 주세요.'; }
    return describeError(e, '요청을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
}

export function isDocsConsentRequired(e) {
    return e?.code === 'functions/failed-precondition' && e?.details?.code === 'docs-consent-required';
}

// 업로드 전 클라이언트 검사 — 서버도 같은 검사를 하지만 20MB를 올리고 나서 거절당하는 낭비를 막는다
export function validateDocFile(file) {
    if (!file) return '파일을 선택해 주세요.';
    if (!DOC_ALLOWED_TYPES.includes(file.type)) return 'PDF, JPEG, PNG 파일만 올릴 수 있습니다.';
    if (file.size > DOC_MAX_BYTES) return '파일은 20MB 이하만 올릴 수 있습니다.';
    if (file.size === 0) return '빈 파일은 올릴 수 없습니다.';
    return '';
}

// D-1
export async function issueDocUploadUrl({ pid, type, contentType, size, issuedAt }) {
    // 서버 정본 키 kind·sizeBytes (계약 §1-1). type·size 도 서버가 수용하지만 정본으로 보낸다
    const res = await call('issueDocUploadUrl')({ pid, kind: type, contentType, sizeBytes: size, ...(issuedAt ? { issuedAt } : {}) });
    return res.data || {};
}

// D-2 — XHR로 진행률 콜백. 실패는 예외(상태코드 포함)로 올려 화면이 반드시 표시하게 한다
export function uploadDocToSignedUrl({ uploadUrl, method = 'PUT', headers = {}, file, onProgress }) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);
        Object.entries(headers || {}).forEach(([k, v]) => { if (k.toLowerCase() !== 'content-type') xhr.setRequestHeader(k, v); });
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable && onProgress) onProgress(Math.round(ev.loaded / ev.total * 100)); };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else { console.error('[clinicPatientsApi] doc upload failed', xhr.status, xhr.responseText?.slice(0, 200)); reject(new Error(`업로드 실패 (HTTP ${xhr.status})`)); }
        };
        xhr.onerror = () => { console.error('[clinicPatientsApi] doc upload network error'); reject(new Error('업로드 중 네트워크 오류가 발생했습니다.')); };
        xhr.onabort = () => reject(new Error('업로드가 취소되었습니다.'));
        xhr.send(file);
    });
}

// D-3
export async function finalizeClinicPatientDoc(pid, docId) {
    const res = await call('finalizeClinicPatientDoc')({ pid, docId });
    return res.data || {};
}

// D-4
export async function listClinicPatientDocs(pid) {
    const res = await call('listClinicPatientDocs')({ pid });
    // 정본 kind·sizeBytes 우선, 별칭 type·size 폴백 (계약 §1-1 — 서버는 둘 다 반환)
    const docs = (res.data?.docs || []).map(d => ({ ...d, type: d.kind || d.type, size: d.sizeBytes ?? d.size }));
    return { ...(res.data || {}), docs };
}

// D-5
export async function issueDocDownloadUrl(pid, docId) {
    const res = await call('issueDocDownloadUrl')({ pid, docId });
    return res.data || {};
}

// D-6
export async function deleteClinicPatientDoc(pid, docId, reason) {
    if (!DOC_DELETE_REASONS.some(r => r.value === reason)) throw new Error('삭제 사유를 선택해 주세요.');
    const res = await call('deleteClinicPatientDoc')({ pid, docId, reason });
    return res.data || {};
}

// ── 명부 목록 (클라이언트 직접 list — 목록 CF 없음, 계약 §3-6 끝) ──
// 정본: clinicPatientRecords/{치과 회사 uid}/patients (rules :2871-2886, 오너·직원 list 허용)
// 단골(환자앱, 구 스키마): clinicPatients/{hospitalId}/patients (rules :2849-2854, hospitals.claimedBy 오너만)
const recordsCol = (clinicId) => collection(db, 'clinicPatientRecords', clinicId, 'patients');
const legacyCol = (hospitalId) => collection(db, 'clinicPatients', hospitalId, 'patients');

const toDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    const d = typeof v === 'number' ? new Date(v) : (v._seconds ? new Date(v._seconds * 1000) : new Date(v));
    return Number.isNaN(d.getTime()) ? null : d;
};

// 덴클 케어 앱 등록 레코드 판별 (2026-10-04 대표 결정 "앱 등록 즉시 성립" — 계획_환자앱1차_통합 W2·W9)
//   서버 확정(10-04 dev-server, 10-05 찾기 경로 추가): 앱 등록 레코드 = source 'care' · registeredVia 'care_qr'|'care_search' · 등록 시점 phoneLast4 기록. 값이 바뀌면 이 표만 고친다.
//   (구 등록 경로 v2 의 source 'self' 는 운영 미배포 — 앱 등록으로 보지 않는다)
export const APP_REGISTER_SOURCES = ['care'];
export const APP_REGISTER_VIA = ['care_qr', 'care_search'];   // 서버 clinic-patient-self-register.js:532 — QR 'care_qr' / 앱 치과 찾기 'care_search'
export const isAppRegisteredRecord = (r) => !!r && !r.legacy && (r.appRegistered === true || APP_REGISTER_SOURCES.includes(r.source));
// 정본 v2.4-4 #3 L-4 — 앱 등록 배지에 등록 경로(QR/찾기) 표시. pa-clinic-1.1 1항 "등록 경로 표시"의 이행.
//   registeredVia 가 없거나 모르는 값이면 경로를 지어내지 않고 '앱 등록'만.
export const appRegisterBadgeLabel = (r) => {
    const via = r && r.registeredVia;
    if (via === 'care_qr') return '앱 등록(QR)';
    if (via === 'care_search') return '앱 등록(찾기)';
    return '앱 등록';
};

// 두 스키마(정본 name/… · 구 patientName/…)를 화면용 한 형태로 정규화
export function normalizeRecord(id, d, { legacy = false } = {}) {
    const status = d.status || (d.isActive === false ? 'deleted' : 'active');
    const c = d.consents || {};
    return {
        id,
        name: d.name || d.patientName || '',
        phoneLast4: d.phoneLast4 || (d.patientPhone ? String(d.patientPhone).replace(/[^0-9]/g, '').slice(-4) : ''),
        birthDate: d.birthDate || '',
        birthYear: d.patientBirthYear || (d.birthDate ? parseInt(String(d.birthDate).slice(0, 4), 10) : null),
        gender: d.gender || d.patientGender || '',
        note: d.note || '',
        source: d.source || (legacy ? 'favorite' : 'qr'),
        status,
        createdAt: toDate(d.createdAt || d.registeredAt),
        consentOk: !!(c.collect?.agreed && c.sensitive?.agreed && c.delegation?.agreed),
        fcmRegistered: d.fcmRegistered === true,            // 명부 「설치/미설치」 배지 등 기존 화면 표시용 — 의미 유지(true 만 설치)
        appNotMissing: d.fcmRegistered !== false,           // 알림 파트 발송 판정용(2026-10-08 검토 P2-5 — 서버 기준: 명시적 false 만 '앱 없음')
        caseCount: d.caseCount || 0,
        docsConsent: effectiveDocs(d),                          // 서류 수신(cp-1.4 docsPref 우선, 없으면 cp-1.2 consents.docs — 서버 effectiveDocs 동일 규칙)
        docCount: typeof d.docCount === 'number' ? d.docCount : 0,   // 서류 수(서버 유지 필드 — 계약 가정 D-6, 없으면 0 → 배지 미표시)
        deleteRequestedAt: toDate(d.deleteRequestedAt),          // status deleteRequested 일 때만 존재
        deleteRequestReason: d.deleteRequestReason || null,      // 'account_delete' | 'patient_request'
        phonePending: d.phonePending === true,                   // 자가등록 확인 시 Auth 번호가 없어 번호 미기록(계약 v2 §A-1)
        registeredVia: typeof d.registeredVia === 'string' ? d.registeredVia : '',   // 앱 등록 경로(L-4 배지)
        appRegistered: !legacy && (APP_REGISTER_SOURCES.includes(d.source) || APP_REGISTER_VIA.includes(d.registeredVia)),   // 덴클 케어 앱 등록(위 판별과 같은 규칙)
        aftercareOn: !!(c.aftercare?.agreed === true && !c.aftercare?.revokedAt),   // 진료 후 질문 동의(aftercare) — [진료 끝 ▾] 활성 판정(계약_질문로드맵_웹 §2, 서버가 같은 조건으로 재검사)
        // 알림 파트(2026-10-08) 명부 선택 모달 사전 표시용 — 최종 판정은 서버(consentDecision: 문안 해시·2년·치과 일치까지 재검사)
        recallConsentOn: !!(c.recall?.agreed === true && !c.recall?.revokedAt),      // 재내원 안내 동의(recall-1.x) 기록 존재
        recallOptIn: d.recallPref?.enabled === true,                                // 환자가 이 치과 재내원 안내를 켬
        notifyOn: (d.notifyPref && typeof d.notifyPref.enabled === 'boolean') ? d.notifyPref.enabled === true : c.notify?.agreed === true,   // 서버 effectiveNotify 와 같은 규칙
        legacy,
    };
}

export async function listClinicPatientRecords(clinicId) {
    const snap = await getDocs(query(recordsCol(clinicId), orderBy('createdAt', 'desc'), limit(500)));
    return snap.docs.map(d => normalizeRecord(d.id, d.data()));
}

// 앱 자가등록 확인 대기(status 'unconfirmed') 만 — 「오늘」 카드용. where 필터(전체 스캔 금지), 상한 200
//   정본: docs/계약_등록경로v2_서버_2026-10-01.md §A-1(source 'self', unconfirmed 에는 이름·생년월일·성별만 — 번호 없음)
export async function listUnconfirmedClinicPatientRecords(clinicId) {
    const snap = await getDocs(query(recordsCol(clinicId), where('status', '==', 'unconfirmed'), limit(200)));
    return snap.docs.map(d => normalizeRecord(d.id, d.data()))
        .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
}

// 계약 v2 §A-3 — 치과 대표·직원. unconfirmed → active(이때 서버가 Auth 번호 기록). 응답 { ok, pid, status:'active', alreadyActive }
//   오류 not-found / failed-precondition {code:'record-not-confirmable'}(deleteRequested)
export async function confirmClinicPatient(pid) {
    const res = await call('confirmClinicPatient')({ pid });
    return res.data || {};
}

// 계약 v2 §A-3 — 치과 대표·직원. "우리 환자 아님" = unconfirmed 레코드 즉시 삭제(사유 없음). 응답 { ok, pid }
//   오류 not-found / failed-precondition {code:'record-already-confirmed'}(active 는 deleteClinicPatient)
export async function rejectSelfRegistration(pid) {
    const res = await call('rejectSelfRegistration')({ pid });
    return res.data || {};
}

// 의뢰서 환자명 타이핑 제안 (환자앱 환자여정 P1 H1, 2026-09-21 — 계획_환자앱여정_P1 §2 H1)
//   비교 규칙 = 픽커(ClinicPatientPicker.js:28-32)와 동일: active + 필수 동의(consentOk) + 이름 포함. 정규화 = 공백 제거·소문자.
//   법무 조건(법무검토_환자앱여정_P1 §1 H1): 2자 미만이면 조회 자체를 하지 않고 빈 배열.
//   호출자는 cache 객체({ rows })를 넘겨 마운트당 1회만 list 하고 이후는 메모리 필터(키 입력마다 500건 list 방지).
//   rules :2995 list 허용 범위(오너·직원·관리자) 안에서만 — 새 쿼리 경로 없음(listClinicPatientRecords 재사용).
//   환자명은 반환값에만 담기고 console 에는 남기지 않는다.
export const normalizePatientQuery = (v) => String(v || '').replace(/\s+/g, '').toLowerCase();

// 순수 함수 — 로드된 rows 에서 이름 포함 매칭
export function filterRecordsByName(rows, queryText) {
    const q = normalizePatientQuery(queryText);
    if (q.length < 2) return [];
    return (rows || []).filter(r => r.status === 'active' && r.consentOk && normalizePatientQuery(r.name).includes(q));
}

// 동명이인 수 — 이름 정규화(공백 제거·소문자) 키별 인원. 삭제된 레코드는 세지 않는다(W9 "동명이인 주의")
export function sameNameCountMap(rows) {
    const m = new Map();
    (rows || []).forEach(r => {
        if (!r || r.status === 'deleted') return;
        const k = normalizePatientQuery(r.name);
        if (!k) return;
        m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
}
export const sameNameCountOf = (map, name) => (map && map.get(normalizePatientQuery(name))) || 0;

export async function matchRecordsByName(clinicId, queryText, { cache } = {}) {
    if (normalizePatientQuery(queryText).length < 2) return [];   // 2자 미만 = 조회 없음
    if (!clinicId) return [];
    let rows = cache && Array.isArray(cache.rows) ? cache.rows : null;
    if (!rows) {
        rows = await listClinicPatientRecords(clinicId);
        if (cache) cache.rows = rows;
    }
    return filterRecordsByName(rows, queryText);
}

// 환자앱 단골 명부 — hospitals.claimedBy == 치과 오너 uid 인 병원 1곳 (직원은 rules상 못 읽음 → 호출하지 않음)
export async function listLegacyFavorites(ownerUid) {
    const hSnap = await getDocs(query(collection(db, 'hospitals'), where('claimedBy', '==', ownerUid), limit(1)));
    if (hSnap.empty) return { hospitalId: null, rows: [] };
    const hospitalId = hSnap.docs[0].id;
    const pSnap = await getDocs(query(legacyCol(hospitalId), where('isActive', '==', true), orderBy('registeredAt', 'desc'), limit(500)));
    return { hospitalId, rows: pSnap.docs.map(d => normalizeRecord(d.id, d.data(), { legacy: true })) };
}
